import { Component, effect, model, input, output, ViewChild } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { Table } from 'primeng/table';
import { InputText } from 'primeng/inputtext';
import { Button } from 'primeng/button';

import { MunicipioService } from '@/core/api/sicom-api/registro/medico/municipio/municipio.service';
import { MunicipioResponse } from '@/core/api/sicom-api/registro/medico/municipio/municipio.model';

interface MunicipioOption {
    id: number;
    label: string; // dsMunicipio - uf
}

@Component({
    selector: 'app-select-municipio',
    standalone: true,
    imports: [FormsModule, TableModule, InputText, Button],
    templateUrl: './select-municipio.html',
    styleUrls: ['./select-municipio.scss']
})
export class SelectMunicipio {
    // Reference to the PrimeNG table instance for programmatic filtering
    @ViewChild('dt') dt?: Table;
    // Two-way bindable selected municipality id
    idMunicipio = model<number | null>(null);
    disabled = input<boolean>(false);
    // Exibe os botões internos de ação (Selecionar/Cancelar)
    showActions = input<boolean>(true);
    // Mínimo de caracteres exigidos no filtro para habilitar a lista
    minCharsToEnable = input<number>(0);

    // Eventos para o container reagir às ações do componente
    selecionar = output<number | null>();
    cancelar = output<void>();

    options: MunicipioOption[] = [];
    loading = false;
    // selected row in the table
    selectedRow: MunicipioOption | null = null;
    globalFilter = '';

    constructor(private municipioService: MunicipioService) {
        // keep selectedRow synced with external model value
        effect(() => {
            const id = this.idMunicipio();
            if (id != null) {
                this.selectedRow = this.options.find((o) => o.id === id) ?? null;
            } else {
                this.selectedRow = null;
            }
        });

        this.carregarMunicipios();
    }

    onRowSelect(): void {
        this.idMunicipio.set(this.selectedRow?.id ?? null);
    }

    onRowUnselect(): void {
        // Quando desselecionar, limpar o id
        this.idMunicipio.set(this.selectedRow?.id ?? null);
    }

    onSelecionarClick(): void {
        // Emite o id atual (ou null) para o container
        const id = this.selectedRow?.id ?? null;
        this.idMunicipio.set(id);
        this.selecionar.emit(id);
    }

    // Garante que o duplo clique selecione a linha clicada antes de confirmar
    onRowDblClick(row: MunicipioOption): void {
        this.selectedRow = row;
        // Atualiza o texto do campo de edição com o nome do município selecionado
        this.globalFilter = row?.label ?? '';
        // Aplica o filtro global programaticamente (caso a tabela já exista)
        try {
            this.dt?.filterGlobal(this.globalFilter, 'contains');
        } catch {
            // no-op: se a tabela ainda não estiver montada, apenas ignore
        }
        // Confirma a seleção (atualiza id e emite evento)
        this.onSelecionarClick();
    }

    onCancelarClick(): void {
        // Limpa seleção e emite evento de cancelamento
        this.selectedRow = null;
        this.idMunicipio.set(null);
        this.cancelar.emit();
    }

    private carregarMunicipios(): void {
        this.loading = true;
        this.municipioService.listarMunicipios().subscribe({
            next: (lista: MunicipioResponse[]) => {
                this.options = (lista || []).map((m) => ({ id: m.id, label: `${m.dsMunicipio} - ${m.uf ?? ''}`.trim() })).sort((a, b) => a.label.localeCompare(b.label));

                // If there is a preselected id, keep it; else default to null
                const current = this.idMunicipio();
                if (current != null) {
                    const found = this.options.find((o) => o.id === current) || null;
                    this.selectedRow = found;
                    if (!found) this.idMunicipio.set(null);
                } else {
                    this.selectedRow = null;
                }
            },
            error: () => {
                // silent fail; keep empty list
            },
            complete: () => (this.loading = false)
        });
    }

    // Habilita a listagem apenas quando o usuário digitou o mínimo de caracteres
    canEnableList(): boolean {
        const min = this.minCharsToEnable() ?? 0;
        return (this.globalFilter?.trim().length || 0) >= Math.max(0, min);
    }
}
